import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaExchangeAlt } from 'react-icons/fa';
import { useTheme } from '../../context/ThemeContext';
import { MODES, transliterate, slugify, detectScript } from '../../lib/transliterate';
import CopyButton from './CopyButton';
import { buttonClass, fieldClass, labelClass, mutedClass, tabClass } from '../uiStyles';

const SAMPLE = "O'zbekiston Respublikasi — Toshkent shahri";

const Transliterator = () => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const [mode, setMode] = useState('latinToCyrillic');
  const [text, setText] = useState('');

  const output = useMemo(() => transliterate(text, mode), [text, mode]);
  const slug = useMemo(() => slugify(text), [text]);

  // Nudges the user when the pasted script does not match the chosen direction.
  const mismatch = text.trim()
    && ((mode === 'latinToCyrillic' && detectScript(text) === 'cyrillic')
      || (mode === 'cyrillicToLatin' && detectScript(text) === 'latin'));

  const swap = () => {
    setMode((current) => (current === 'cyrillicToLatin' ? 'latinToCyrillic' : 'cyrillicToLatin'));
    setText(output);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {MODES.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => setMode(option)}
            className={tabClass(isDarkMode, mode === option)}
          >
            {t(`useful.translit.modes.${option}`)}
          </button>
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className={labelClass(isDarkMode)} htmlFor="translit-input">
            {t('useful.translit.input')}
          </label>
          <textarea
            id="translit-input"
            value={text}
            onChange={(event) => setText(event.target.value)}
            rows={8}
            placeholder={SAMPLE}
            className={`${fieldClass(isDarkMode)} resize-y font-mono text-sm`}
          />
        </div>
        <div>
          <label className={labelClass(isDarkMode)} htmlFor="translit-output">
            {t('useful.translit.output')}
          </label>
          <textarea
            id="translit-output"
            value={output}
            readOnly
            rows={8}
            className={`${fieldClass(isDarkMode)} resize-y font-mono text-sm`}
          />
        </div>
      </div>

      {mismatch && (
        <p className="text-sm text-amber-500">{t('useful.translit.mismatch')}</p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <CopyButton value={output} />
        <button
          type="button"
          onClick={swap}
          className={`${buttonClass(isDarkMode)} inline-flex items-center gap-2`}
          disabled={mode === 'latinToAscii'}
        >
          <FaExchangeAlt /> {t('useful.swap')}
        </button>
        <button type="button" onClick={() => setText(SAMPLE)} className={buttonClass(isDarkMode)}>
          {t('useful.translit.sample')}
        </button>
        <button type="button" onClick={() => setText('')} className={buttonClass(isDarkMode)}>
          {t('useful.clear')}
        </button>
      </div>

      {slug && (
        <p className={`text-sm ${mutedClass(isDarkMode)}`}>
          {t('useful.translit.slug')}: <code className="font-mono">{slug}</code>
        </p>
      )}
    </div>
  );
};

export default Transliterator;
